import { useEffect, useState } from "react";
import { useTranslationApp } from "../../../lib/i18next";
import { Box, Menu, MenuItem, IconButton, alpha } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import Checkbox from "../../../components/Form/Checkbox";
import { GRAY_400 } from "../../../utils/colors";
import {
  COLUMN_NAME_CONFIGURE_COLUMNS,
  COLUMN_NAME_IMPRESSIONS,
  COLUMN_NAME_CPM,
  COLUMN_NAME_CLICKS,
  COLUMN_NAME_CPC,
  COLUMN_NAME_CTR,
  COLUMN_NAME_RESULTS,
  COLUMN_NAME_CPA,
  COLUMN_NAME_SPEND,
  ROAS_METRICS,
  FREQUENCY,
} from "../../../i18n/keysTranslations";
import {
  startGetHomeMetricsConfiguration,
  startSetHomeMetricsConfiguration,
} from "../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../lib/redux";

const columnsCheckboxes = [
  { key: "impressions", label: COLUMN_NAME_IMPRESSIONS },
  { key: "cpm", label: COLUMN_NAME_CPM },
  { key: "clicks", label: COLUMN_NAME_CLICKS },
  { key: "cpc", label: COLUMN_NAME_CPC },
  { key: "ctr", label: COLUMN_NAME_CTR },
  { key: "results", label: COLUMN_NAME_RESULTS },
  { key: "cpa", label: COLUMN_NAME_CPA },
  { key: "spend", label: COLUMN_NAME_SPEND },
  { key: "roas", label: ROAS_METRICS },
  { key: "frequency", label: FREQUENCY },
];

const COLUMN_VISIBILITY_KEYS = columnsCheckboxes.map(({ key }) => key);

const pickColumnVisibilityFromConfig = (config = {}) => {
  const picked = {};

  COLUMN_VISIBILITY_KEYS.forEach((key) => {
    if (typeof config[key] === "boolean") {
      picked[key] = config[key];
    }
  });

  return picked;
};

const ColumnVisibilityMenu = ({
  columnVisibility,
  onToggleColumn = () => {},
  onSetColumnVisibility = () => {},
  sx = {},
}) => {
  const { t } = useTranslationApp();
  const [anchorEl, setAnchorEl] = useState(null);

  const dispatch = useDispatchApp();

  const onClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const onToggleColumnInternal = (column) => {
    onToggleColumn(column);
    dispatch(
      startSetHomeMetricsConfiguration({
        metricsConfiguration: {
          [column]: !columnVisibility[column],
        },
      })
    );
  };

  const onGetMetricsConfiguration = async () => {
    const response = await dispatch(startGetHomeMetricsConfiguration());

    if (response.ok) {
      onSetColumnVisibility((prev) => ({
        ...prev,
        ...pickColumnVisibilityFromConfig(response.data),
      }));
    } else {
      dispatch(
        startSetHomeMetricsConfiguration({
          metricsConfiguration:
            pickColumnVisibilityFromConfig(columnVisibility),
        })
      );
    }
  };

  useEffect(() => {
    onGetMetricsConfiguration();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <IconButton
        size="small"
        edge="start"
        onClick={onClick}
        sx={{
          color: "#000",
          width: 35,
          height: 35,
          "&:hover": {
            bgcolor: alpha("#000", 0.1),
            color: "#000",
          },
          ...sx,
        }}
      >
        <SettingsOutlinedIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        sx={{
          zIndex: 2000,
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            border: "1px solid rgba(0, 0, 0, 0.15)",
            mt: 1,
          },
        }}
      >
        <MenuItem
          sx={{
            backgroundColor: "transparent",
            color: "#000000",
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {t(COLUMN_NAME_CONFIGURE_COLUMNS)}
        </MenuItem>
        <MenuItem
          sx={{
            backgroundColor: "transparent",
            borderBottom: "1px solid rgba(0, 0, 0, 0.15)",
          }}
        />
        {columnsCheckboxes.map(({ key, label }) => (
          <MenuItem
            key={key}
            dense
            onClick={() => onToggleColumnInternal(key)}
            sx={{
              backgroundColor: "transparent",
              color: "#000000",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                lineHeight: 0,
              }}
            >
              <Checkbox
                checked={columnVisibility[key]}
                borderColor={GRAY_400}
                color="#000000"
              />
            </Box>
            <Box
              component="span"
              sx={{
                color: "#000000",
                fontSize: "0.875rem",
                mt: 0.1,
                display: "flex",
                alignItems: "center",
              }}
            >
              {t(label)}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ColumnVisibilityMenu;
